<script setup>
// Posiłek: nazwa, moment, status, skład i wartości odżywcze. Można zacząć od
// zapisanego posiłku albo złożyć skład ręcznie — wtedy makra liczą się z niego.
// Bez składu makra wpisuje się wprost.
import { computed, ref } from 'vue';
import { currencySymbol } from '@/composables/useCurrency.js';
import WhenFields from '@/components/v2/add/when-fields.vue';
import IngredientList from '@/components/v2/add/ingredient-list.vue';
import SelectField from '@/components/v2/select-field.vue';
import ProductPicker from '@/components/v2/product-picker.vue';
import { pickedWeight } from '@/composables/usePortions.js';
import { useI18n } from 'vue-i18n';

const props = defineProps({
    draft: { type: Object, required: true },
    errors: { type: Object, default: () => ({}) },
    meals: { type: Array, default: () => [] },
    products: { type: Array, default: () => [] },
    isEdit: { type: Boolean, default: false },
});
const emit = defineEmits(['pick-meal', 'pick-product', 'update-ingredients', 'set-mode']);
const { t } = useI18n();

// Jednostki zgodne z API: makra w gramach, sól w miligramach, cena w złotówkach.
const nutrients = computed(() => [
    { key: 'calories', label: t('entry.meal.nutrients.calories'), unit: 'kcal' },
    { key: 'protein', label: t('entry.meal.nutrients.protein'), unit: 'g' },
    { key: 'carbs', label: t('entry.meal.nutrients.carbs'), unit: 'g' },
    { key: 'fat', label: t('entry.meal.nutrients.fat'), unit: 'g' },
    { key: 'saturated_fat', label: t('entry.meal.nutrients.saturated_fat'), unit: 'g' },
    { key: 'sugar', label: t('entry.meal.nutrients.sugar'), unit: 'g' },
    { key: 'fiber', label: t('entry.meal.nutrients.fiber'), unit: 'g' },
    { key: 'salt', label: t('entry.meal.nutrients.salt'), unit: 'mg' },
]);

const fromList = computed(() => props.draft.ingredientsMode === 'list');

const sortedMeals = computed(() =>
    [...props.meals].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'pl')));

const selectedMeal = computed({
    get: () => props.draft.mealUuid,
    set: (uuid) => emit('pick-meal', uuid),
});

// Start od produktu: wybór z listy od razu dokłada go do składu w podanej wadze,
// więc nie trzeba osobnego „Dodaj" — wagę i tak można potem poprawić w składzie.
// Reagujemy na jawny wybór (`pick`), nie na to, że wpisany tekst pasuje do nazwy —
// inaczej dopisanie pełnej nazwy dodawałoby produkt drugi raz. Produkt liczony
// w sztukach przy nieruszonej wadze wchodzi jako 1 sztuka.
const productQuery = ref('');
const pickedProduct = ref(null);
const productWeight = ref('100');

const onPickProduct = (product) => {
    emit('pick-product', { product, weight: pickedWeight(product, productWeight.value) });
    productQuery.value = '';
    pickedProduct.value = null;
};
</script>

<template>
    <div>
        <!-- Start z katalogu: cały zapisany posiłek albo pojedynczy produkt -->
        <!-- Jedno pod drugim: w wąskim modalu dwie kolumny ucinały etykiety -->
        <div v-if="!isEdit && (sortedMeals.length || products.length)" class="flex flex-col gap-3 mb-3">
            <div v-if="sortedMeals.length">
                <label class="v2-label" for="v2-meal-pick">{{ t('entry.meal.fromSaved') }}</label>
                <SelectField
                    id="v2-meal-pick"
                    v-model="selectedMeal"
                    :options="sortedMeals.map(m => ({ value: m.uuid, label: m.name }))"
                    :empty-label="t('entry.meal.manualEntry')"
                    :search-placeholder="t('entry.meal.searchMeal')"
                />
            </div>

            <div v-if="products.length" class="flex items-end gap-2">
                <ProductPicker
                    v-model="productQuery"
                    v-model:product="pickedProduct"
                    :products="products"
                    id="v2-meal-product"
                    :label="t('entry.meal.singleProduct')"
                    :placeholder="t('entry.meal.searchProduct')"
                    class="flex-1 min-w-0"
                    @pick="onPickProduct"
                />
                <span class="w-[76px] shrink-0">
                    <label class="v2-label" for="v2-meal-product-weight">{{ t('entry.meal.weightGrams') }}</label>
                    <input
                        id="v2-meal-product-weight"
                        v-model="productWeight"
                        type="number"
                        min="1"
                        inputmode="numeric"
                        class="v2-input v2-input--num text-right"
                    />
                </span>
            </div>
        </div>

        <input
            v-model="draft.name"
            type="text"
            class="v2-freetext mb-1"
            :placeholder="t('entry.meal.namePlaceholder')"
            :aria-invalid="!!errors.name"
        />
        <p v-if="errors.name" class="text-[11.5px] text-[#B5643C] mb-2">{{ errors.name }}</p>

        <div class="border-t border-[#EFE9DF] pt-4 mt-4 mb-4">
            <WhenFields :draft="draft" :errors="errors" />
        </div>

        <div class="mb-4">
            <span class="v2-label">{{ t('entry.meal.status') }}</span>
            <div class="inline-flex rounded-lg border border-[#E4DDD1] p-0.5 bg-[#FAF7F2]">
                <button
                    v-for="s in [{ v: 'eaten', l: t('entry.meal.eaten') }, { v: 'planned', l: t('entry.meal.planned') }]"
                    :key="s.v"
                    type="button"
                    class="px-3 py-1.5 rounded-[7px] text-[12.5px] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#C0764B]"
                    :class="draft.status === s.v ? 'bg-[#211E1A] text-[#FBF9F4] font-semibold' : 'text-[#6B655C] hover:text-[#211E1A]'"
                    @click="draft.status = s.v"
                >{{ s.l }}</button>
            </div>
        </div>

        <!-- Skład: aktywny tryb decyduje, skąd biorą się makra -->
        <div class="border-t border-[#EFE9DF] pt-4 mb-4">
            <IngredientList
                v-if="fromList"
                :ingredients="draft.ingredients"
                :products="products"
                @update="list => emit('update-ingredients', list)"
            />
            <button
                v-else
                type="button"
                class="text-[12.5px] font-semibold text-[#B5643C] hover:text-[#8E4B2C] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C0764B] rounded"
                @click="emit('set-mode', 'list')"
            >
                {{ draft.hadIngredients ? t('entry.meal.editIngredients') : t('entry.meal.addIngredients') }}
            </button>
        </div>

        <div class="flex items-baseline justify-between gap-3 mb-2">
            <span class="text-[9px] font-bold tracking-[0.18em] text-[#A29A8E]">
                {{ fromList ? t('entry.meal.valuesFromIngredients') : t('entry.meal.nutritionValues') }}
            </span>
            <button
                v-if="fromList"
                type="button"
                class="text-[11.5px] text-[#A29A8E] hover:text-[#211E1A] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C0764B] rounded"
                @click="emit('set-mode', 'manual')"
            >
                {{ t('entry.meal.enterMacrosManually') }}
            </button>
        </div>

        <div class="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-2.5">
            <div v-for="n in nutrients" :key="n.key">
                <label class="v2-label" :for="`v2-${n.key}`">{{ n.label }} <span class="text-[#B8B0A3]">{{ n.unit }}</span></label>
                <input
                    :id="`v2-${n.key}`"
                    v-model="draft[n.key]"
                    type="number"
                    min="0"
                    inputmode="numeric"
                    class="v2-input v2-input--num"
                    :class="fromList ? '!bg-[#F2ECE1] !text-[#8A8378]' : ''"
                    :readonly="fromList"
                    :tabindex="fromList ? -1 : undefined"
                    placeholder="—"
                />
            </div>
            <div>
                <label class="v2-label" for="v2-price">{{ t('entry.meal.price') }} <span class="text-[#B8B0A3]">{{ currencySymbol() }}</span></label>
                <input
                    id="v2-price"
                    v-model="draft.price"
                    type="number"
                    min="0"
                    step="0.01"
                    inputmode="decimal"
                    class="v2-input v2-input--num"
                    :class="fromList ? '!bg-[#F2ECE1] !text-[#8A8378]' : ''"
                    :readonly="fromList"
                    :tabindex="fromList ? -1 : undefined"
                    placeholder="—"
                />
            </div>
        </div>

        <p v-if="fromList" class="text-[11px] text-[#B8B0A3] mt-2">
            {{ t('entry.meal.computedHint') }}
        </p>
    </div>
</template>
